// Admin Application Detail — full view, inline edit, status change, approve/deny,
// documents, requested-docs list, status history.
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, errorMessage } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import AiScoreBadge from "../../components/AiScoreBadge";
import StatusBadge from "../../components/StatusBadge";
import DocumentsPanel from "../../components/DocumentsPanel";
import PreApprovalPanel from "../../components/PreApprovalPanel";
import { ArrowLeft, Check, X, Send, Pencil, Save, Mail } from "lucide-react";
import { fmtCurrency, fmtDate, fmtDateTime, LOAN_TYPE_LABELS, LOAN_PROGRAMS } from "../../lib/constants";
import { toast } from "sonner";

const STATUS_OPTIONS = [
  "sponsor_review", "submitted_to_admin", "ok_to_apply", "docs_requested",
  "application_sent", "under_review", "approved", "denied",
];

const REQUESTED_DOC_LABEL = {
  executed_contract: "Executed Contract",
  drivers_license: "Driver's License / ID",
  credit_report: "Credit Report",
  bank_statement: "Bank Statements",
  other: "Other",
};

const EDITABLE_FIELDS = [
  ["borrower_name", "Borrower Name", "text"],
  ["borrower_email", "Borrower Email", "email"],
  ["borrower_phone", "Borrower Phone", "text"],
  ["property_address", "Property Address", "text"],
  ["property_type", "Property Type", "select-property"],
  ["loan_type", "Loan Type", "select-loan"],
  ["purchase_price", "Purchase Price ($)", "number"],
  ["expected_loan_amount", "Expected Loan ($)", "number"],
  ["estimated_rehab_budget", "Rehab Budget ($)", "number"],
  ["arv", "ARV ($)", "number"],
  ["credit_score", "Credit Score", "number"],
  ["capital_available", "Capital Available ($)", "number"],
  ["num_projects", "# Projects", "number"],
  ["beds", "Beds", "number"],
  ["baths", "Baths", "number"],
  ["living_sqft", "Living SqFt", "number"],
  ["notes", "Notes", "textarea"],
];

const PROPERTY_TYPES = ["single_family", "multifamily", "condo", "commercial", "land"];

export default function AdminApplicationDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [app, setApp] = useState(null);
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);

  const [statusOpen, setStatusOpen] = useState(false);
  const [status, setStatus] = useState("");
  const [statusNote, setStatusNote] = useState("");
  const [savingStatus, setSavingStatus] = useState(false);

  const [denyOpen, setDenyOpen] = useState(false);
  const [denyNote, setDenyNote] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/applications/${id}`);
      setApp(data);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  useEffect(() => { load(); }, [id]);

  const beginEdit = () => {
    const draft = {};
    EDITABLE_FIELDS.forEach(([k]) => { draft[k] = app[k] ?? ""; });
    setEdit(draft);
    setEditing(true);
  };

  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      // Coerce numbers
      const payload = { ...edit };
      EDITABLE_FIELDS.forEach(([k, , t]) => {
        if (t === "number") payload[k] = Number(payload[k] || 0);
      });
      const { data } = await api.patch(`/admin/applications/${id}`, payload);
      setApp(data);
      setEditing(false);
      toast.success("Application updated");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSavingEdit(false);
    }
  };

  const openStatusDialog = (preset) => {
    setStatus(preset || app.status);
    setStatusNote("");
    setStatusOpen(true);
  };

  const changeStatus = async (statusOverride, noteOverride) => {
    setSavingStatus(true);
    try {
      await api.post(`/admin/applications/${id}/status`, {
        status: statusOverride ?? status,
        note: noteOverride ?? statusNote,
      });
      toast.success("Status updated");
      setStatusOpen(false);
      setDenyOpen(false);
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSavingStatus(false);
    }
  };

  const requestDocs = async () => {
    try {
      await api.post(`/admin/applications/${id}/request-docs`);
      toast.success("Documents requested from borrower");
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const approve = () => changeStatus("approved", "Application approved by admin");
  const submitDeny = () => changeStatus("denied", denyNote || "Application denied by admin");

  if (!app) return <div className="text-sm text-slate-400">Loading…</div>;

  const ai = app.ai_score || { total: 0, breakdown: {} };
  const brk = ai.breakdown || {};
  const sourceIsBroker = app.submitted_by === "sponsor";

  return (
    <div className="space-y-6 animate-fade-in-up">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <button
            onClick={() => nav(-1)}
            className="mb-2 inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-200"
            data-testid="admin-detail-back-btn"
          >
            <ArrowLeft className="h-3 w-3" /> Back
          </button>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-100" data-testid="admin-detail-borrower-name">
            {app.borrower_name}
          </h1>
          <div className="mt-1 text-sm text-slate-400">{app.property_address}</div>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
            <Badge
              variant="outline"
              className={
                sourceIsBroker
                  ? "border-sky-500/30 bg-sky-500/10 text-sky-300"
                  : "border-violet-500/30 bg-violet-500/10 text-violet-300"
              }
              data-testid="admin-detail-source-badge"
            >
              {sourceIsBroker ? "Submitted by broker" : "Submitted by borrower"}
            </Badge>
            <span className="text-slate-400">·</span>
            <span className="text-slate-300" data-testid="admin-detail-submitter-name">
              {app.submitted_by_name || (sourceIsBroker ? app.sponsor_name : app.borrower_name)}
            </span>
            {sourceIsBroker && app.sponsor_name && (
              <>
                <span className="text-slate-400">·</span>
                <span className="text-slate-400">Broker: {app.sponsor_name}</span>
              </>
            )}
            <span className="text-slate-400">·</span>
            <span className="text-slate-500">Created {fmtDate(app.created_at)}</span>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <AiScoreBadge score={ai} testId="admin-detail-ai-score" />
          <StatusBadge status={app.status} testId="admin-detail-status" />
        </div>
      </div>

      {/* Action bar */}
      <div className="flex flex-wrap items-center gap-2 rounded-md border border-slate-800 bg-slate-900 p-3" data-testid="admin-action-bar">
        <Button
          onClick={approve}
          disabled={savingStatus || app.status === "approved"}
          className="bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50"
          data-testid="admin-approve-btn"
        >
          <Check className="mr-2 h-4 w-4" /> Approve
        </Button>
        <Button
          onClick={() => { setDenyNote(""); setDenyOpen(true); }}
          disabled={savingStatus || app.status === "denied"}
          variant="outline"
          className="border-rose-500/40 bg-rose-500/10 text-rose-200 hover:bg-rose-500/20 disabled:opacity-50"
          data-testid="admin-deny-btn"
        >
          <X className="mr-2 h-4 w-4" /> Deny
        </Button>
        <Button
          onClick={requestDocs}
          variant="outline"
          className="border-slate-700 bg-slate-950 text-slate-100 hover:bg-slate-800"
          data-testid="admin-request-docs-btn"
        >
          <Mail className="mr-2 h-4 w-4" /> Request docs
        </Button>
        <Button
          onClick={() => openStatusDialog()}
          variant="outline"
          className="border-slate-700 bg-slate-950 text-slate-100 hover:bg-slate-800"
          data-testid="admin-change-status-btn"
        >
          <Send className="mr-2 h-4 w-4" /> Change status
        </Button>
        {!editing ? (
          <Button
            onClick={beginEdit}
            variant="outline"
            className="border-slate-700 bg-slate-950 text-slate-100 hover:bg-slate-800 ml-auto"
            data-testid="admin-edit-btn"
          >
            <Pencil className="mr-2 h-4 w-4" /> Edit
          </Button>
        ) : (
          <div className="ml-auto flex gap-2">
            <Button
              onClick={() => setEditing(false)}
              variant="outline"
              disabled={savingEdit}
              className="border-slate-700 bg-slate-950 text-slate-100 hover:bg-slate-800"
              data-testid="admin-edit-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={saveEdit}
              disabled={savingEdit}
              className="bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-60"
              data-testid="admin-edit-save-btn"
            >
              <Save className="mr-2 h-4 w-4" /> {savingEdit ? "Saving…" : "Save changes"}
            </Button>
          </div>
        )}
      </div>

      {/* Details + AI */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="border-slate-800 bg-slate-900 p-6 lg:col-span-2">
          <div className="mb-4 text-sm font-semibold">Application Details</div>
          {editing ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {EDITABLE_FIELDS.map(([k, lbl, t]) => (
                <EditField
                  key={k}
                  field={k}
                  label={lbl}
                  type={t}
                  value={edit[k]}
                  onChange={(v) => setEdit((e) => ({ ...e, [k]: v }))}
                />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <Detail label="Email" value={app.borrower_email} />
              <Detail label="Phone" value={app.borrower_phone || "—"} />
              <Detail label="Loan Type" value={LOAN_TYPE_LABELS[app.loan_type] || app.loan_type} />
              <Detail label="Property Type" value={app.property_type} />
              <Detail label="Purchase Price" value={fmtCurrency(app.purchase_price)} />
              <Detail label="Expected Loan" value={fmtCurrency(app.expected_loan_amount)} />
              <Detail label="Rehab Budget" value={fmtCurrency(app.estimated_rehab_budget)} />
              <Detail label="ARV" value={fmtCurrency(app.arv)} />
              <Detail label="Credit Score" value={app.credit_score} />
              <Detail label="Capital Available" value={fmtCurrency(app.capital_available)} />
              <Detail label="# Projects" value={app.num_projects} />
              <Detail label="LLC" value={app.property_in_llc ? (app.llc_name || "Yes") : "No"} />
              <Detail label="Beds / Baths" value={`${app.beds} / ${app.baths}`} />
              <Detail label="Sq Ft" value={app.living_sqft || "—"} />
              <Detail label="Submitted By" value={sourceIsBroker ? `Broker · ${app.sponsor_name || app.submitted_by_name}` : "Borrower (self)"} />
              <Detail label="Updated" value={app.updated_at ? fmtDateTime(app.updated_at) : "—"} />
              {app.notes && (
                <div className="sm:col-span-2">
                  <div className="text-[11px] uppercase tracking-widest text-slate-500">Notes</div>
                  <div className="mt-1 text-sm text-slate-100 whitespace-pre-wrap">{app.notes}</div>
                </div>
              )}
            </div>
          )}
        </Card>

        <Card className="border-slate-800 bg-slate-900 p-6">
          <div className="mb-4 text-sm font-semibold">AI Pre-Qualification</div>
          <div className="mb-6">
            <div className="text-5xl font-semibold tabular-nums">{ai.total}</div>
            <div className="mt-1 text-xs uppercase tracking-widest text-slate-500">{ai.category}</div>
          </div>
          <ScoreRow label="Credit Score" value={brk.credit_score || 0} max={30} />
          <ScoreRow label="Experience" value={brk.experience || 0} max={25} />
          <ScoreRow label="Deal Numbers" value={brk.deal_numbers || 0} max={25} />
          <ScoreRow label="Capital & LLC" value={brk.capital_llc || 0} max={10} />
          <ScoreRow label="Property Info" value={brk.property_info || 0} max={10} />
        </Card>
      </div>

      {/* Requested docs (if any) */}
      {(app.requested_docs || []).length > 0 && (
        <Card className="border-slate-800 bg-slate-900 p-6" data-testid="admin-detail-requested-docs">
          <div className="mb-3 text-sm font-semibold">Documents requested from borrower</div>
          <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {app.requested_docs.map((r, i) => (
              <li key={`${r.doc_type}-${r.label || i}`} className="rounded-md border border-slate-800 bg-slate-950 p-3 text-sm">
                <div className="font-medium text-slate-100">
                  {REQUESTED_DOC_LABEL[r.doc_type] || r.doc_type}
                </div>
                {r.label && <div className="mt-0.5 text-xs text-slate-400">{r.label}</div>}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* Pre-approval letters */}
      <PreApprovalPanel app={app} />

      {/* Documents */}
      <DocumentsPanel
        applicationId={id}
        canUpload={true}
        title="Documents"
        subtitle="Uploads from broker, borrower, or admin all appear here."
      />

      {/* Status history */}
      <Card className="border-slate-800 bg-slate-900 p-6" data-testid="admin-status-history">
        <div className="mb-4 text-sm font-semibold">Status History</div>
        <div className="space-y-3">
          {(app.status_history || []).map((h, i) => (
            <div key={`${h.status}-${h.at || i}`} className="flex items-start gap-4 border-l-2 border-emerald-500/30 pl-4">
              <div>
                <StatusBadge status={h.status} />
                <div className="mt-1 text-xs text-slate-500">{fmtDateTime(h.at)}</div>
                {h.note && <div className="mt-1 text-sm text-slate-300">{h.note}</div>}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Change status dialog */}
      <Dialog open={statusOpen} onOpenChange={setStatusOpen}>
        <DialogContent className="border-slate-800 bg-slate-900 text-slate-50">
          <DialogHeader>
            <DialogTitle>Change Application Status</DialogTitle>
            <DialogDescription className="text-slate-400">
              {app.borrower_name} · {app.property_address}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">New Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="mt-1 border-slate-800 bg-slate-950" data-testid="admin-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                  {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300">Note (optional)</Label>
              <Textarea
                value={statusNote}
                onChange={(e) => setStatusNote(e.target.value)}
                className="mt-1 border-slate-800 bg-slate-950 text-slate-50 min-h-[90px]"
                data-testid="admin-status-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setStatusOpen(false)}
              className="border-slate-700 bg-slate-950 text-slate-100 hover:bg-slate-800"
            >
              Cancel
            </Button>
            <Button
              onClick={() => changeStatus()}
              disabled={savingStatus}
              className="bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-60"
              data-testid="admin-status-save-btn"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deny dialog */}
      <Dialog open={denyOpen} onOpenChange={setDenyOpen}>
        <DialogContent className="border-slate-800 bg-slate-900 text-slate-50">
          <DialogHeader>
            <DialogTitle>Deny application</DialogTitle>
            <DialogDescription className="text-slate-400">
              This will mark the application as denied and email the borrower and broker.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-slate-300">Reason (optional)</Label>
            <Textarea
              value={denyNote}
              onChange={(e) => setDenyNote(e.target.value)}
              className="mt-1 border-slate-800 bg-slate-950 text-slate-50 min-h-[90px]"
              placeholder="e.g. Credit score below program minimums"
              data-testid="admin-deny-note"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDenyOpen(false)}
              className="border-slate-700 bg-slate-950 text-slate-100 hover:bg-slate-800"
            >
              Cancel
            </Button>
            <Button
              onClick={submitDeny}
              disabled={savingStatus}
              className="bg-rose-600 text-white hover:bg-rose-500 disabled:opacity-60"
              data-testid="admin-deny-confirm-btn"
            >
              Deny application
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="mt-1 text-sm text-slate-100">{value ?? "—"}</div>
    </div>
  );
}

function ScoreRow({ label, value, max }) {
  const pct = Math.min(100, (value / max) * 100);
  return (
    <div className="mb-3">
      <div className="mb-1 flex items-center justify-between text-xs">
        <span className="text-slate-300">{label}</span>
        <span className="tabular-nums text-slate-100">{value}/{max}</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-slate-800">
        <div className="h-full rounded-full bg-emerald-500" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function EditField({ field, label, type, value, onChange }) {
  const INP = "mt-1 border-slate-800 bg-slate-950 text-slate-50";
  if (type === "textarea") {
    return (
      <div className="sm:col-span-2">
        <Label className="text-slate-300">{label}</Label>
        <Textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className={`${INP} min-h-[100px]`}
          data-testid={`admin-edit-${field}`}
        />
      </div>
    );
  }
  if (type === "select-loan") {
    return (
      <div>
        <Label className="text-slate-300">{label}</Label>
        <Select value={value || "fix_and_flip"} onValueChange={onChange}>
          <SelectTrigger className={INP} data-testid={`admin-edit-${field}`}><SelectValue /></SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
            {LOAN_PROGRAMS.map((p) => <SelectItem key={p.key} value={p.key}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    );
  }
  if (type === "select-property") {
    return (
      <div>
        <Label className="text-slate-300">{label}</Label>
        <Select value={value || "single_family"} onValueChange={onChange}>
          <SelectTrigger className={INP} data-testid={`admin-edit-${field}`}><SelectValue /></SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
            {PROPERTY_TYPES.map((p) => <SelectItem key={p} value={p}>{p.replace(/_/g, " ")}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    );
  }
  return (
    <div>
      <Label className="text-slate-300">{label}</Label>
      <Input
        type={type === "number" ? "number" : type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className={INP}
        data-testid={`admin-edit-${field}`}
      />
    </div>
  );
}
